import React, { useEffect } from 'react';
import { i18nFn, useAuthPin, useSpringBigStore } from '@springbig/shared';
import { FormControl, HStack, Input, Pressable, VStack, WarningIcon } from 'native-base';
import { Caption, H3, H2 } from '~/shared/Typography';
import { useFormStyles, useGeneralStyles } from '~/shared/styles';
import { AppRouteProps, useAppNavigate } from '~/pages';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '~/shared/Button';
import { NavigateOptions, Params } from 'react-router';

type BirthdayStepProps = {
    t: i18nFn;
    onEvent: (message: string) => void;
    navigate: (route: AppRouteProps, opts?: NavigateOptions) => void;
    params: Params<string>;
};

export const BirthdayStep = ({
    t,
    onEvent,
    navigate, 
    params,
}: BirthdayStepProps) => {

    const gStyle = useGeneralStyles();
    const { birthdays, birthdayInput } = useSpringBigStore();

    const { mutate: authPin, error, isError, isLoading } = useAuthPin();

    if (!navigate) {
        navigate = useAppNavigate().navigate;
    }

    if (!params) {
        params = useAppNavigate().params;
    }

    const submitBirthday = (birthday: string): void => {

        authPin({
            birthday,
            stashboardOrMemberKey: params.wallet_id,
        }, {
            onSuccess: (response) => {

                if (response.step) {
                    onEvent(response.step);
                }
            }
        });
    };

    useEffect(() => {

        if (!birthdays) {
            navigate(`w/${params.wallet_id}/`);
        }
    }, [birthdays]);

    const {
        input_background,
        input_border,
        input_label,
        input_text
    } = useFormStyles();

    const schema = yup.object().shape({
        birthday: yup
            .string()
            .required('birthday is required')
            .trim()
            .matches(
                /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/,
                'Birthday must be in the format MM/DD/YYYY'
            )
    });

    const { control, handleSubmit, setError, clearErrors, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const { field: birthdayField } = useController({
        control,
        defaultValue: '',
        name: 'birthday',
    });

    const onSubmit = (data: FormData): void => {

        const birthday = data.birthday;
        authPin({
            birthday,
            stashboardOrMemberKey: params.wallet_id,
        }, {
            onSuccess: (response) => {

                if (response.step) {
                    onEvent(response.step);
                }
            }
        });
    };

    const onBirthdayChange = (value: string): void => {

        clearErrors('birthday');
        const numericValue = value.replace(/\D/g, '');

        let formattedValue = numericValue.replace(/^(.{2})/, '$1/');
        formattedValue = formattedValue.replace(/^(.{5})/, '$1/');
        const limitedValue = formattedValue.slice(0, 10);

        birthdayField.onChange(limitedValue);
    };

    return (
        birthdayInput === 'freehand' ?
            <VStack backgroundColor={gStyle.screen_background} space={4} maxWidth={64}>
                <VStack textAlign={'center'}>
                    <H2>please confirm your birthday</H2>
                    <Caption>enter your birthday in order to continue</Caption>
                </VStack>
                <FormControl isInvalid={!!errors.birthday}>
                    <FormControl.Label color={input_label}>birthday</FormControl.Label>
                    <Input
                        {...birthdayField}
                        backgroundColor={input_background}
                        borderColor={input_border}
                        height={10}
                        fontSize={16}
                        color={input_text}
                        isInvalid={!!errors.birthday}
                        isDisabled={isLoading}
                        onChangeText={onBirthdayChange}
                        maxLength={255}
                        returnKeyType='done'
                        placeholder={'MM/DD/YYYY'}
                    />
                    <FormControl.ErrorMessage
                        leftIcon={<WarningIcon size={3} />}
                    >
                        { errors.birthday?.message }
                    </FormControl.ErrorMessage>
                    { isError &&
                        <Caption
                            fontSize={12}
                            color={gStyle.button_danger_background}
                            textAlign={'center'}
                        >
                            { (error?.data?.error) as any }
                        </Caption>
                    }
                </FormControl>
                <Button
                    bg={gStyle.button_background}
                    _text={{ color: gStyle.button_text }}
                    onPress={handleSubmit(onSubmit)}
                    isLoading={isLoading}
                >
                confirm birthday
                </Button>
            </VStack>
            :
            birthdays && birthdays.length > 0 ?

                <VStack backgroundColor={gStyle.screen_background} space={8} w={'clamp(220px, 100vw, 380px)'} alignItems={'center'}>
                    <VStack>
                        <H3 fontSize={20} fontFamily="Helvetica" fontWeight='bold'>
                            { t('auth.ten_dlc.birthday.title') }
                        </H3>
                        <Caption maxW={'100%'} fontFamily="Helvetica">
                            { t('auth.ten_dlc.birthday.description') }
                        </Caption>
                    </VStack>
                    <Caption maxW={'100%'} my={-4} fontFamily="Helvetica" textAlign="center" color='grey'>
                        { t('auth.ten_dlc.birthday.verify') }
                    </Caption>
                    { isError &&
                        <Caption
                            fontSize={12}
                            color={gStyle.button_danger_background}
                            textAlign={'center'}
                            fontFamily="Helvetica"
                        >
                            { (error?.data?.error) as any }
                        </Caption>
                    }
                    <VStack space={4}>
                        <HStack
                            space={4}
                            flexWrap={'wrap'}
                        >
                            { birthdays.slice(0, 2).map((birthday, index) =>

                                <Pressable
                                    key={index}
                                    onPress={() => submitBirthday(birthday)}
                                    bg={gStyle.button_background}
                                    px={4}
                                    py={2}
                                    flex={1}
                                    rounded={'md'}
                                >
                                    <Caption
                                        color={gStyle.button_text}
                                        fontSize={18}
                                        fontWeight={'300'}
                                        w={'110px'}
                                        textAlign={'center'}
                                        fontFamily="Helvetica"
                                    >
                                        { birthday }
                                    </Caption>
                                </Pressable>
                            )}
                        </HStack>
                        <HStack
                            space={4}
                            flexWrap={'wrap'}
                        >
                            { birthdays.slice(2, 4).map((birthday, index) =>

                                <Pressable
                                    key={index}
                                    onPress={() => submitBirthday(birthday)}
                                    bg={gStyle.button_background}
                                    px={4}
                                    py={2}
                                    flex={1}
                                    rounded={'md'}
                                >
                                    <Caption
                                        color={gStyle.button_text}
                                        fontSize={18}
                                        fontWeight={'300'}
                                        w={'110px'}
                                        textAlign={'center'}
                                    >
                                        { birthday }
                                    </Caption>
                                </Pressable>
                            )}
                        </HStack>
                    </VStack>
                </VStack>
                :
                <Caption>An error occurred, could not fetch birthday from database</Caption>
    );
};
